// @ts-nocheck
import { forwardRef } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Progress,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { Paper } from '../../../ui';
import { FadeIn } from '../../../animations';
import type { GaugeProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * LinearGauge - Linear progress bar gauge
 *
 * Displays progress as a horizontal bar with threshold markers.
 */
const LinearGauge = forwardRef<HTMLDivElement, GaugeProps>(
  (
    {
      title,
      value,
      max,
      min = 0,
      unit,
      description,
      thresholds,
      showPercentage = true,
      showValue = true,
      size = 'md',
      variant = 'default',
      color,
      animate = true,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const sizes = {
      xs: { height: 8, fontSize: 'xs', spacing: 'xs' },
      sm: { height: 12, fontSize: 'sm', spacing: 'sm' },
      md: { height: 16, fontSize: 'md', spacing: 'md' },
      lg: { height: 20, fontSize: 'lg', spacing: 'lg' },
      xl: { height: 24, fontSize: 'xl', spacing: 'xl' },
    };

    const currentSize = sizes[size];

    // Calculate percentage
    const percentage = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);

    // Determine color
    const getGaugeColor = () => {
      if (color) return color;

      if (thresholds && thresholds.length > 0) {
        const sortedThresholds = [...thresholds].sort((a, b) => b.value - a.value);
        for (const threshold of sortedThresholds) {
          if (value >= threshold.value) {
            return threshold.color;
          }
        }
        return sortedThresholds[sortedThresholds.length - 1].color;
      }

      if (percentage >= 70) return 'green';
      if (percentage >= 30) return 'yellow';
      return 'red';
    };

    const gaugeColor = getGaugeColor();

    return (
      <FadeIn>
        <Paper ref={ref} variant={variant} p={currentSize.spacing} className={className}>
          <Stack gap={currentSize.spacing}>
            <Group justify="space-between" align="center">
              {title && (
                <Text size={currentSize.fontSize} fw={600} style={accessibilityStyles}>
                  {title}
                </Text>
              )}

              {(showValue || showPercentage) && (
                <Group gap="xs">
                  {showValue && (
                    <Text size={currentSize.fontSize} fw={700} style={accessibilityStyles}>
                      {value}{unit}
                    </Text>
                  )}
                  {showPercentage && (
                    <Text size="sm" c="dimmed" style={accessibilityStyles}>
                      ({percentage.toFixed(1)}%)
                    </Text>
                  )}
                </Group>
              )}
            </Group>

            <Box style={{ position: 'relative' }}>
              <Progress
                value={percentage}
                color={gaugeColor}
                size={currentSize.height}
                animated={animate}
              />

              {/* Threshold markers */}
              {thresholds && thresholds.map((threshold, idx) => {
                const thresholdPercentage = ((threshold.value - min) / (max - min)) * 100;
                return (
                  <Box
                    key={idx}
                    style={{
                      position: 'absolute',
                      left: `${thresholdPercentage}%`,
                      top: -4,
                      width: 2,
                      height: currentSize.height + 8,
                      backgroundColor: threshold.color,
                      opacity: 0.6,
                    }}
                  />
                );
              })}
            </Box>

            {description && (
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {description}
              </Text>
            )}

            {thresholds && thresholds.length > 0 && (
              <Group gap="md" style={{ flexWrap: 'wrap' }}>
                {thresholds.map((threshold, idx) => (
                  <Group key={idx} gap={6}>
                    <Box
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        backgroundColor: threshold.color,
                      }}
                    />
                    <Text size="xs">
                      {threshold.label || `≥ ${threshold.value}${unit}`}
                    </Text>
                  </Group>
                ))}
              </Group>
            )}
          </Stack>
        </Paper>
      </FadeIn>
    );
  }
);

LinearGauge.displayName = 'LinearGauge';

export default LinearGauge;
